import { useCallback, useState } from 'react';
import { toast } from 'react-toastify';
import { useSaveAndCountQuery } from '../../../../../hooks/QueryBuilder';
import { isQueryRefusal } from '../../../../../hooks/QueryBuilder/useSaveQuery';
import type { TargetingFormData } from '../CampaignTargetingStep';
import type { TargetingBuilder } from './useTargetingBuilder';
import { formatRecipients, settleToast } from './toasts';

type CountOutcome = 'done' | 'waiting' | 'failed';

export const useTargetingCount = (builder: TargetingBuilder, onChange: (patch: Partial<TargetingFormData>) => void) => {
  const { saveAndCountQuery, loading: counting } = useSaveAndCountQuery();
  const [waitingQueryId, setWaitingQueryId] = useState<number | null>(null);
  const { isValid, groups, mode, selectedGroupId, descriptionRef } = builder;

  const count = useCallback(async (waitForResult: boolean): Promise<CountOutcome> => {
    const description = descriptionRef.current.trim();
    if (!isValid) {
      toast.warning('Veuillez compléter toutes les règles');
      return 'failed';
    }
    if (!description) {
      toast.warning('Veuillez renseigner un nom de requête');
      return 'failed';
    }

    const toastId = toast.loading('Comptage de la requête en cours...');
    try {
      const result = await saveAndCountQuery({
        description,
        groups,
        groupId: selectedGroupId && selectedGroupId > 0 ? selectedGroupId : undefined,
        source: mode === 'simple' ? 'SIMPLE' : 'ADVANCED',
      });
      // Le ciblage de campagne crée toujours une requête, sans nom unique : aucun refus n'arrive ici
      if (!result || isQueryRefusal(result)) {
        settleToast(toastId, 'error', 'Erreur lors du comptage');
        return 'failed';
      }

      const selection = { queryDescription: description, listId: null, cashdeskTarget: null };
      if (result.mode === 'sync') {
        onChange({ ...selection, queryId: result.queryId, nbRecipients: result.count });
        settleToast(toastId, 'success', `Requête comptée : ${formatRecipients(result.count)}`);
        return 'done';
      }

      onChange({ ...selection, queryId: result.queryId ?? null, nbRecipients: null });
      if (result.queryId && waitForResult) {
        toast.dismiss(toastId);
        setWaitingQueryId(result.queryId);
        return 'waiting';
      }
      settleToast(toastId, 'info', 'Comptage lancé : vous serez notifié à la fin du traitement');
      return 'done';
    } catch {
      settleToast(toastId, 'error', 'Erreur lors du comptage');
      return 'failed';
    }
  }, [descriptionRef, isValid, groups, mode, selectedGroupId, saveAndCountQuery, onChange]);

  return { count, counting, waitingQueryId, stopWaiting: () => setWaitingQueryId(null) };
};
